import { get, set, del, keys } from 'idb-keyval';
import type { StoredDesign } from '../types';

const SCHEMA_VERSION = 1;
const SCHEMA_VERSION_KEY = '__schema_version__';

/**
 * Initialize DB: run migrations if schema version changed
 */
export async function initDb(): Promise<void> {
  const storedVersion = await get<number>(SCHEMA_VERSION_KEY);

  if (storedVersion === undefined) {
    // First run
    await set(SCHEMA_VERSION_KEY, SCHEMA_VERSION);
    return;
  }

  if (storedVersion < SCHEMA_VERSION) {
    await runMigrations(storedVersion, SCHEMA_VERSION);
    await set(SCHEMA_VERSION_KEY, SCHEMA_VERSION);
  }
}

async function runMigrations(from: number, to: number): Promise<void> {
  console.log(`[DB] Migrating schema v${from} → v${to}`);
  // Future migrations go here
}

// ── Design CRUD ──────────────────────────────────────────────────────────────

const DESIGN_PREFIX = 'design:';

function designKey(id: string): string {
  return `${DESIGN_PREFIX}${id}`;
}

export async function saveDesign(design: StoredDesign): Promise<void> {
  await set(designKey(design.id), {
    ...design,
    _schemaVersion: SCHEMA_VERSION,
  });
}

export async function loadDesign(id: string): Promise<StoredDesign | undefined> {
  return get<StoredDesign>(designKey(id));
}

export async function deleteDesign(id: string): Promise<void> {
  await del(designKey(id));
}

export async function listDesigns(): Promise<StoredDesign[]> {
  const allKeys = await keys<string>();
  const designKeys = allKeys.filter((k) => k.startsWith(DESIGN_PREFIX));

  const designs = await Promise.all(designKeys.map((k) => get<StoredDesign>(k)));
  return (designs.filter(Boolean) as StoredDesign[]).sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function clearAllDesigns(): Promise<void> {
  const allKeys = await keys<string>();
  const designKeys = allKeys.filter((k) => k.startsWith(DESIGN_PREFIX));
  await Promise.all(designKeys.map((k) => del(k)));
}
